import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./ManageBonusFundsPage.css";

const PAGE_LIMIT = 20;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const SEARCH_DEBOUNCE_MS = 600;
const EXCEL_SHEET_TITLE = "Bonus Funds";

const APPROVE_MODES = [
  {
    value: "instant",
    label: "Instant",
    desc: "Credit full vested funds to the user's wallet immediately.",
    icon: "fa-bolt",
  },
  {
    value: "vesting",
    label: "Vesting",
    desc: "Release funds gradually according to the active release plan.",
    icon: "fa-hourglass-half",
  },
];

const isPendingApproval = (row) =>
  String(row?.fundApproval?.status || "").toLowerCase() === "pending";

const userIdOf = (row) => row?._id || row?.userId || row?.id;

const num = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "object" && v.$numberDecimal != null) return Number(v.$numberDecimal);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (v) => {
  const n = num(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 8 });
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const fullName = (u) => {
  const parts = [u?.firstName, u?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const KYC_MAP = {
  0: { label: "Not verified", cls: "off" },
  1: { label: "Pending", cls: "pending" },
  2: { label: "Verified", cls: "on" },
};

const kycInfo = (v) => KYC_MAP[Number(v)] || { label: String(v ?? "—"), cls: "off" };

const fundStatusCls = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "approved") return "on";
  if (s === "rejected") return "off";
  return "pending";
};

const bonusFundsRowToExport = (row, currency) => {
  const tasks = row.tasks || {};
  const earn = row.referralEarnings || {};
  const vest = row.vestingWallet || {};
  const fund = row.fundApproval || {};
  const kyc = kycInfo(row.kycVerified);
  return {
    UUID: row.uuid ?? "",
    Name: fullName(row) === "—" ? "" : fullName(row),
    Email: row.emailId ?? "",
    Mobile: row.mobileNumber ?? "",
    "Referral Code": row.referral_code ?? "",
    KYC: kyc.label,
    "Reward Claimed": row.rewardClaimed ? "Yes" : "No",
    "Reward Claimed At": row.rewardClaimedAt ?? "",
    "Tasks Completed": tasks.completed_count ?? 0,
    "Tasks Total": tasks.total_tasks ?? 0,
    "Social Tasks Completed": tasks.socialTasksCompleted ? "Yes" : "No",
    "Incomplete Task IDs": Array.isArray(tasks.incomplete_task_ids)
      ? tasks.incomplete_task_ids.join(", ")
      : "",
    "Signup Bonus Received": num(row.signupBonusReceived),
    "Referral Total Earned": num(earn.totalEarned),
    "Referral Count": earn.totalReferralCount ?? 0,
    "Signup Bonus Total": num(earn.signup_bonus?.total),
    "Signup Bonus Count": earn.signup_bonus?.count ?? 0,
    "Referral Bonus Total": num(earn.referral_bonus?.total),
    "Referral Bonus Count": earn.referral_bonus?.count ?? 0,
    "Vesting Total Earned": num(vest.totalEarned),
    "Vesting Locked": num(vest.locked),
    "Vesting Released": num(vest.released),
    "Vesting Available": num(vest.available),
    "Fund Approval Status": fund.status ?? "",
    "Fund Approval Mode": fund.mode ?? "",
    "Fund Approved At": fund.approvedAt ?? "",
    "Fund Approved By": fund.approvedBy ?? "",
    Currency: currency || "ZILN",
    "Registered By": row.registeredBy ?? "",
    "Created At": row.createdAt ?? "",
    "User ID": row._id ?? row.userId ?? row.id ?? "",
  };
};

const ManageBonusFundsPage = () => {
  const [rows, setRows] = useState([]);
  const [currency, setCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [approveRow, setApproveRow] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [pageLimit]);

  const fetchRewardDetails = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: pageLimit };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await AdminService.getReferralRewardDetails(params);
      if (res?.success !== false) {
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(list);
        setCurrency(res?.meta?.currencyShortName || "");
        setTotalPages(res?.pagination?.total_pages || 1);
        setTotal(typeof res?.pagination?.total === "number" ? res.pagination.total : null);
        if (res?.success === false && !list.length) {
          setError(res?.message || "Failed to load reward details.");
        }
      } else {
        setRows([]);
        setError(res?.message || "Failed to load reward details.");
      }
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || err?.message || "Failed to load reward details.");
    } finally {
      setLoading(false);
    }
  }, [page, pageLimit, debouncedSearch]);

  useEffect(() => {
    fetchRewardDetails();
  }, [fetchRewardDetails]);

  const openApprove = (row) => {
    setDetailRow(null);
    setApproveRow(row);
  };

  const handleApproved = () => {
    setApproveRow(null);
    fetchRewardDetails();
  };

  const fetchAllRewardDetails = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const params = { page: currentPage, limit: EXPORT_BATCH };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await AdminService.getReferralRewardDetails(params);
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllRewardDetails();
      if (!allRows.length) {
        toast.error("No reward details match your filters to export.");
        return;
      }

      const exportCur = currency || allRows[0]?.currency || "ZILN";
      const exportData = allRows.map((row) => bonusFundsRowToExport(row, exportCur));
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `bonus_funds_${dateStamp}.xlsx`);
      toast.success(`Exported ${allRows.length} row(s) to Excel.`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to export Excel file.");
    } finally {
      setExportLoading(false);
    }
  };

  const cur = currency || "ZILN";

  const pageStart = total === 0 || total == null ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd =
    total === 0 || total == null
      ? rows.length
      : Math.min(page * pageLimit, total);

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || PAGE_LIMIT);
  };

  return (
    <main className="dashboard-main dashboard-subpage manage-bonus-funds-page">
      <section className="dashboard-hero bonus-funds-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Referral</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Manage bonus funds</span>
          </p>
          <h1 className="dashboard-hero__title">Manage Bonus Funds</h1>
          <p className="dashboard-hero__subtitle">
            Referral reward, signup bonus and vesting wallet details per user
          </p>
        </div>
        <div className="bonus-funds-hero__icon" aria-hidden>
          <i className="fas fa-hand-holding-usd" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="bonus-funds-card">
          <div className="bonus-funds-card__head">
            <div className="bonus-funds-card__title-wrap">
              <span className="bonus-funds-card__icon">
                <i className="fas fa-gift" />
              </span>
              <h2 className="bonus-funds-card__title">Users reward details</h2>
            </div>
            <div className="bonus-funds-card__meta">
              <span className="bonus-funds-chip">
                <i className="fas fa-coins" /> {cur}
              </span>
              <span className="bonus-funds-stat">
                <strong>{rows.length}</strong> on page
                {total != null && (
                  <>
                    {" "}
                    (<strong>{total}</strong> total)
                  </>
                )}
              </span>
              <button
                type="button"
                className="bonus-funds-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered reward details as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          <div className="bonus-funds-toolbar">
            <div className="bonus-funds-search">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Search by name, email, UUID, mobile or referral code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="bonus-funds-msg bonus-funds-msg--error">{error}</p>}

          {loading ? (
            <div className="bonus-funds-loading">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="bonus-funds-empty">No reward details to show.</p>
          ) : (
            <>
              <div className="bonus-funds-table-wrap">
                <table className="bonus-funds-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>User</th>
                      <th>Referral code</th>
                      <th>KYC</th>
                      <th>Reward claimed</th>
                      <th>Tasks</th>
                      <th>Signup bonus</th>
                      <th>Referral earned</th>
                      <th>Vesting (locked / released / avail)</th>
                      <th>Fund approval</th>
                      <th>Registered</th>
                      <th className="bonus-funds-table__action-col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const rid = r._id || r.uuid;
                      const kyc = kycInfo(r.kycVerified);
                      const tasks = r.tasks || {};
                      const earn = r.referralEarnings || {};
                      const vest = r.vestingWallet || {};
                      const fund = r.fundApproval || {};
                      return (
                        <tr key={rid}>
                          <td className="bonus-funds-table__uuid">{r.uuid || "—"}</td>
                          <td>
                            <div className="bonus-funds-user">
                              <strong>{fullName(r)}</strong>
                              <span>{r.emailId || "—"}</span>
                              {r.mobileNumber ? <span>{r.mobileNumber}</span> : null}
                            </div>
                          </td>
                          <td>
                            <code className="bonus-funds-code">{r.referral_code || "—"}</code>
                          </td>
                          <td>
                            <span className={`bonus-funds-badge bonus-funds-badge--${kyc.cls}`}>
                              {kyc.label}
                            </span>
                          </td>
                          <td>
                            {r.rewardClaimed ? (
                              <span
                                className="bonus-funds-badge bonus-funds-badge--on"
                                title={r.rewardClaimedAt ? formatDate(r.rewardClaimedAt) : ""}
                              >
                                Claimed
                              </span>
                            ) : (
                              <span className="bonus-funds-badge bonus-funds-badge--off">No</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`bonus-funds-tasks ${
                                tasks.socialTasksCompleted ? "bonus-funds-tasks--done" : ""
                              }`}
                            >
                              {tasks.completed_count ?? 0}/{tasks.total_tasks ?? 0}
                            </span>
                          </td>
                          <td className="bonus-funds-table__num">
                            {formatAmount(r.signupBonusReceived)} {cur}
                          </td>
                          <td className="bonus-funds-table__num">
                            {formatAmount(earn.totalEarned)} {cur}
                            <span className="bonus-funds-sub">
                              {earn.totalReferralCount ?? 0} refs
                            </span>
                          </td>
                          <td className="bonus-funds-table__num">
                            <span className="bonus-funds-vest">
                              <span className="bonus-funds-vest--locked">
                                {formatAmount(vest.locked)}
                              </span>
                              <span>/</span>
                              <span className="bonus-funds-vest--released">
                                {formatAmount(vest.released)}
                              </span>
                              <span>/</span>
                              <span className="bonus-funds-vest--avail">
                                {formatAmount(vest.available)}
                              </span>
                            </span>
                          </td>
                          <td>
                            <span
                              className={`bonus-funds-badge bonus-funds-badge--${fundStatusCls(
                                fund.status
                              )}`}
                            >
                              {fund.status || "—"}
                            </span>
                            {fund.mode ? (
                              <span className="bonus-funds-sub">{fund.mode}</span>
                            ) : null}
                          </td>
                          <td className="bonus-funds-table__date">{formatDate(r.createdAt)}</td>
                          <td className="bonus-funds-table__action-col">
                            <div className="bonus-funds-actions">
                              <button
                                type="button"
                                className="bonus-funds-btn-view"
                                onClick={() => setDetailRow(r)}
                              >
                                <i className="fas fa-eye" /> Details
                              </button>
                              {isPendingApproval(r) && (
                                <button
                                  type="button"
                                  className="bonus-funds-btn-approve"
                                  onClick={() => openApprove(r)}
                                  disabled={!userIdOf(r)}
                                >
                                  <i className="fas fa-check" /> Approve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(total != null ? total > 0 : rows.length > 0) && (
                <div className="bonus-funds-pagination">
                  <label className="bonus-funds-page-limit bonus-funds-page-limit--footer">
                    <span>Rows per page</span>
                    <select
                      value={pageLimit}
                      onChange={handlePageLimitChange}
                      disabled={loading}
                    >
                      {PAGE_LIMIT_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="bonus-funds-pagination__info">
                    {total != null ? (
                      <>
                        Showing {pageStart}–{pageEnd} of {total} · Page {page} of {totalPages}
                      </>
                    ) : (
                      <>Page {page} of {totalPages}</>
                    )}
                  </span>
                  <div className="bonus-funds-pagination__nav">
                    <button
                      type="button"
                      className="bonus-funds-btn-outline"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fas fa-chevron-left" /> Previous
                    </button>
                    <button
                      type="button"
                      className="bonus-funds-btn-outline"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {detailRow && (
        <BonusFundsDetailModal
          row={detailRow}
          currency={cur}
          onClose={() => setDetailRow(null)}
          onApprove={() => openApprove(detailRow)}
        />
      )}

      {approveRow && (
        <ApproveFundsModal
          row={approveRow}
          currency={cur}
          onClose={() => setApproveRow(null)}
          onApproved={handleApproved}
        />
      )}
    </main>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="bonus-funds-detail__item">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

const BonusFundsDetailModal = ({ row, currency, onClose, onApprove }) => {
  const tasks = row.tasks || {};
  const earn = row.referralEarnings || {};
  const vest = row.vestingWallet || {};
  const fund = row.fundApproval || {};
  const kyc = kycInfo(row.kycVerified);
  const pending = isPendingApproval(row);
  const incomplete = useMemo(
    () => (Array.isArray(tasks.incomplete_task_ids) ? tasks.incomplete_task_ids : []),
    [tasks.incomplete_task_ids]
  );

  return (
    <div
      className="bonus-funds-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bonus-funds-detail-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bonus-funds-modal">
        <div className="bonus-funds-modal__head">
          <div>
            <h2 id="bonus-funds-detail-title">{fullName(row)}</h2>
            <p className="bonus-funds-modal__sub">
              {row.emailId || "—"}
              {row.uuid ? ` · ${row.uuid}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="bonus-funds-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="bonus-funds-modal__body">
          <section className="bonus-funds-detail__section">
            <h3>Profile</h3>
            <dl className="bonus-funds-detail__grid">
              <DetailItem label="Name" value={fullName(row)} />
              <DetailItem label="Email" value={row.emailId || "—"} />
              <DetailItem label="Mobile" value={row.mobileNumber || "—"} />
              <DetailItem label="UUID" value={row.uuid || "—"} />
              <DetailItem label="Referral code" value={row.referral_code || "—"} />
              <DetailItem label="Registered by" value={row.registeredBy || "—"} />
              <DetailItem
                label="KYC"
                value={
                  <span className={`bonus-funds-badge bonus-funds-badge--${kyc.cls}`}>
                    {kyc.label}
                  </span>
                }
              />
              <DetailItem label="Created" value={formatDate(row.createdAt)} />
            </dl>
          </section>

          <section className="bonus-funds-detail__section">
            <h3>Reward & tasks</h3>
            <dl className="bonus-funds-detail__grid">
              <DetailItem
                label="Reward claimed"
                value={
                  row.rewardClaimed ? (
                    <span className="bonus-funds-badge bonus-funds-badge--on">Yes</span>
                  ) : (
                    <span className="bonus-funds-badge bonus-funds-badge--off">No</span>
                  )
                }
              />
              <DetailItem label="Claimed at" value={formatDate(row.rewardClaimedAt)} />
              <DetailItem
                label="Social tasks"
                value={tasks.socialTasksCompleted ? "Completed" : "Incomplete"}
              />
              <DetailItem
                label="Tasks progress"
                value={`${tasks.completed_count ?? 0} / ${tasks.total_tasks ?? 0}`}
              />
              <DetailItem
                label="Incomplete task IDs"
                value={incomplete.length ? incomplete.join(", ") : "None"}
              />
              <DetailItem
                label="Signup bonus received"
                value={`${formatAmount(row.signupBonusReceived)} ${currency}`}
              />
            </dl>
          </section>

          <section className="bonus-funds-detail__section">
            <h3>Referral earnings</h3>
            <dl className="bonus-funds-detail__grid">
              <DetailItem
                label="Total earned"
                value={`${formatAmount(earn.totalEarned)} ${currency}`}
              />
              <DetailItem
                label="Signup bonus"
                value={`${formatAmount(earn.signup_bonus?.total)} ${currency} (${
                  earn.signup_bonus?.count ?? 0
                })`}
              />
              <DetailItem
                label="Referral bonus"
                value={`${formatAmount(earn.referral_bonus?.total)} ${currency} (${
                  earn.referral_bonus?.count ?? 0
                })`}
              />
              <DetailItem label="Total referrals" value={earn.totalReferralCount ?? 0} />
            </dl>
          </section>

          <section className="bonus-funds-detail__section">
            <h3>Vesting wallet</h3>
            <dl className="bonus-funds-detail__grid">
              <DetailItem
                label="Total earned"
                value={`${formatAmount(vest.totalEarned)} ${currency}`}
              />
              <DetailItem label="Locked" value={`${formatAmount(vest.locked)} ${currency}`} />
              <DetailItem label="Released" value={`${formatAmount(vest.released)} ${currency}`} />
              <DetailItem
                label="Available"
                value={`${formatAmount(vest.available)} ${currency}`}
              />
            </dl>
          </section>

          <section className="bonus-funds-detail__section">
            <h3>Fund approval</h3>
            <dl className="bonus-funds-detail__grid">
              <DetailItem
                label="Status"
                value={
                  <span
                    className={`bonus-funds-badge bonus-funds-badge--${fundStatusCls(fund.status)}`}
                  >
                    {fund.status || "—"}
                  </span>
                }
              />
              <DetailItem label="Mode" value={fund.mode || "—"} />
              <DetailItem label="Approved at" value={formatDate(fund.approvedAt)} />
              <DetailItem label="Approved by" value={fund.approvedBy || "—"} />
            </dl>
          </section>
        </div>

        {pending && (
          <div className="bonus-funds-modal__foot">
            <p className="bonus-funds-modal__foot-note">
              <i className="fas fa-circle-info" /> Fund approval is pending for this user.
            </p>
            <button type="button" className="bonus-funds-btn-approve" onClick={onApprove}>
              <i className="fas fa-check" /> Approve funds
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ApproveFundsModal = ({ row, currency, onClose, onApproved }) => {
  const [mode, setMode] = useState("instant");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const userId = userIdOf(row);
  const vest = row.vestingWallet || {};

  const submit = async () => {
    if (!userId) {
      setError("Missing user id for this row.");
      return;
    }
    if (!mode) {
      setError("Select an approval mode.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.approveReferralFunds(userId, { mode });
      if (res?.success) {
        setResult(res.data || {});
        toast.success(res.message || "User funds approved.");
        setTimeout(() => onApproved(), 1200);
      } else {
        setError(res?.message || "Approval failed.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Approval failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bonus-funds-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bonus-funds-approve-title"
      style={{ zIndex: 1060 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="bonus-funds-modal bonus-funds-modal--sm">
        <div className="bonus-funds-modal__head">
          <div>
            <h2 id="bonus-funds-approve-title">Approve bonus funds</h2>
            <p className="bonus-funds-modal__sub">
              {fullName(row)}
              {row.uuid ? ` · ${row.uuid}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="bonus-funds-modal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="bonus-funds-modal__body">
          {result ? (
            <div className="bonus-funds-approve-result">
              <i className="fas fa-circle-check" />
              <h3>Funds approved</h3>
              <dl className="bonus-funds-detail__grid">
                <DetailItem label="Mode" value={result.mode || mode} />
                <DetailItem
                  label="Released amount"
                  value={`${formatAmount(result.releasedAmount)} ${
                    result.currencyShortName || currency
                  }`}
                />
                <DetailItem label="Released rounds" value={result.releasedRounds ?? "—"} />
                <DetailItem label="Vestings completed" value={result.vestingsCompleted ?? "—"} />
              </dl>
            </div>
          ) : (
            <>
              <div className="bonus-funds-approve-summary">
                <span>
                  Vesting available
                  <strong>
                    {formatAmount(vest.available)} {currency}
                  </strong>
                </span>
                <span>
                  Locked
                  <strong>
                    {formatAmount(vest.locked)} {currency}
                  </strong>
                </span>
                <span>
                  Released
                  <strong>
                    {formatAmount(vest.released)} {currency}
                  </strong>
                </span>
              </div>

              <p className="bonus-funds-approve-label">Select approval mode</p>
              <div className="bonus-funds-mode-options">
                {APPROVE_MODES.map((m) => (
                  <button
                    type="button"
                    key={m.value}
                    className={`bonus-funds-mode ${
                      mode === m.value ? "bonus-funds-mode--active" : ""
                    }`}
                    onClick={() => setMode(m.value)}
                    disabled={loading}
                  >
                    <span className="bonus-funds-mode__icon">
                      <i className={`fas ${m.icon}`} />
                    </span>
                    <span className="bonus-funds-mode__text">
                      <strong>{m.label}</strong>
                      <span>{m.desc}</span>
                    </span>
                    {mode === m.value && (
                      <i className="fas fa-check-circle bonus-funds-mode__check" />
                    )}
                  </button>
                ))}
              </div>

              {error && (
                <p className="bonus-funds-msg bonus-funds-msg--error" style={{ marginTop: "0.75rem" }}>
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        {!result && (
          <div className="bonus-funds-modal__foot">
            <button
              type="button"
              className="bonus-funds-btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bonus-funds-btn-approve"
              onClick={submit}
              disabled={loading || !mode}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Approving…
                </>
              ) : (
                <>
                  <i className="fas fa-check" /> Confirm approval
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBonusFundsPage;
